import { useMemo } from 'react'
import { FunnelChartWrapper, type FunnelStage } from '../charts/FunnelChart'
import { WidgetFrame } from './WidgetFrame'
import { useWidgetData } from './useWidgetData'
import { WIDGET_KEY, type WidgetProps } from './types'
import { buildDrillRsql } from '../drill'
import { formatCount } from '../format'
import { runExport, type ExportColumn } from '../../../lib/export'
import { buildPipelineStages, OMITTED_STAGE_LABELS, type PipelineStage } from '../portfolioPipeline'
import type { OutcomePathBucket, OutcomeVarianceResult, StatusCountBucket } from '../../../types/insights'

const EXPORT_COLUMNS: ExportColumn<PipelineStage>[] = [
  { id: 'label', header: 'Stage', toText: (s) => s.label },
  { id: 'count', header: 'Count', toText: (s) => formatCount(s.count) },
]

/**
 * Portfolio pipeline funnel by lifecycle stage — FRONTEND-ONLY, composed entirely from three
 * widget endpoints the dashboard already fetches for other tiles (`filings-by-status`,
 * `monetization-outcomes`, `outcome-variance`; see `../portfolioPipeline.ts` for the honest scope
 * note on why only 4 of the per-Case `deriveLifecycle`'s 6 stages are shown here). No new backend
 * read; no per-Case scan.
 *
 * `filings-by-status` is this tile's backbone (it backs 2 of the 4 stages, `Assessment`/
 * `Certified`) and drives the tile's own loading/error/not_authorized frame; the other two sources
 * degrade independently — either one being unavailable simply omits the ONE stage it backs (with a
 * disclosure note), never fabricates a 0 and never blanks the whole tile (AC26, one level down from
 * the dashboard's own per-widget isolation).
 */
export function PortfolioPipelineFunnelWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const filings = useWidgetData<StatusCountBucket[]>(WIDGET_KEY.filingsByStatus, filter, skipRefetch)
  const monetization = useWidgetData<OutcomePathBucket[]>(WIDGET_KEY.monetizationOutcomes, filter, skipRefetch)
  const outcome = useWidgetData<OutcomeVarianceResult>(WIDGET_KEY.outcomeVariance, filter, skipRefetch)

  const stages = useMemo(
    () =>
      buildPipelineStages(
        filings.status === 'ok' ? filings.data : null,
        monetization.status === 'ok' ? monetization.data : null,
        outcome.status === 'ok' ? outcome.data : null,
      ),
    [filings.status, filings.data, monetization.status, monetization.data, outcome.status, outcome.data],
  )

  const funnelStages: FunnelStage[] = stages.map((s) => ({ key: s.key, label: s.label, count: s.count }))

  function retryAll(): void {
    filings.retry()
    monetization.retry()
    outcome.retry()
  }

  const frameState =
    filings.status === 'loading'
      ? 'loading'
      : filings.status === 'not_authorized'
        ? 'not_authorized'
        : filings.status === 'error'
          ? 'error'
          : stages.length === 0
            ? 'empty'
            : 'ok'

  const degradedNotes: string[] = []
  if (filings.status === 'ok' && monetization.status !== 'ok') {
    degradedNotes.push('"Monetized" omitted — monetization data unavailable right now.')
  }
  if (filings.status === 'ok' && outcome.status !== 'ok') {
    degradedNotes.push('"Filed / Outcome" omitted — outcome data unavailable right now.')
  }

  return (
    <WidgetFrame
      title="Portfolio pipeline by lifecycle stage"
      icon="activity"
      state={frameState}
      message={filings.message}
      onRetry={retryAll}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={stages.length > 0 ? { onClick: () => runExport('csv', 'Portfolio pipeline by lifecycle stage', stages, EXPORT_COLUMNS) } : undefined}
    >
      <FunnelChartWrapper title="Portfolio pipeline by lifecycle stage" stages={funnelStages} />
      <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
        {OMITTED_STAGE_LABELS.join(' and ')} aren&apos;t shown here — no portfolio-level count aggregate exists yet
        for either signal (each is only available per-case, on the Case Lifecycle Progress Tracker).
        {degradedNotes.length > 0 ? ` ${degradedNotes.join(' ')}` : ''}
      </p>
    </WidgetFrame>
  )
}
